import React, { useState } from 'react';
import { Alert, Button, ConfigProvider, Flex, message, Radio, Tabs, Tooltip, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
// @ts-expect-error: No types available for react-copy-to-clipboard
import { CopyToClipboard } from 'react-copy-to-clipboard';
import TextArea from 'antd/es/input/TextArea';
import QRCode from 'react-qr-code';
import { EThemeNames, themePresets } from '../../../domain/types/themePresets';

interface Props {
  authorizationCode: string;
  impersonatedUntil: number;
}

const SCHEMES = { dev: 'leedpulse-dev', staging: 'leedpulse-dev', prod: 'leedpulse' } as const;

const getThemeName = (): EThemeNames => {
  const stored = localStorage.getItem('lf-theme');
  return stored && Object.values(EThemeNames).includes(stored as EThemeNames)
    ? (stored as EThemeNames)
    : EThemeNames.LEEDFLOW_LIGHT;
};

export const getThemePrimaryColor = (): string | undefined => themePresets[getThemeName()].token?.colorPrimary;

const LoginAsMobileModalContent: React.FC<Props> = ({ authorizationCode, impersonatedUntil }) => {
  const [environment, setEnvironment] = useState<'dev' | 'staging' | 'prod'>('prod');
  const [platform, setPlatform] = useState<'ios' | 'android'>('ios');

  const deepLink = `${SCHEMES[environment]}://login?token=${encodeURIComponent(authorizationCode)}`;
  const command =
    platform === 'ios'
      ? `xcrun simctl openurl booted "${deepLink}"`
      : `adb shell am start -a android.intent.action.VIEW -d "${deepLink}"`;

  return (
    <ConfigProvider theme={themePresets[getThemeName()]}>
      <Flex vertical gap={16} style={{ marginTop: 12, maxHeight: '65vh', overflowY: 'auto', marginLeft: -34, width: 'calc(100% + 34px)' }}>
        <Alert
          message={`Session valide jusqu'au ${new Date(impersonatedUntil * 1000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
          type="info"
          showIcon
        />

        <Flex gap={12} wrap justify="center">
          <ConfigProvider theme={{ components: { Tooltip: { colorBgSpotlight: '#2d3436', colorTextLightSolid: '#fff' } } }}>
            <Radio.Group value={environment} onChange={(e) => setEnvironment(e.target.value)} optionType="button" buttonStyle="solid">
              <Tooltip title="Correspond à l'app Prod (App Store) ou au TestFlight Prod">
                <Radio.Button value="prod">Prod</Radio.Button>
              </Tooltip>
              <Tooltip title="Correspond à l'app Staging (TestFlight)">
                <Radio.Button value="staging">Staging</Radio.Button>
              </Tooltip>
              <Tooltip title="Correspond au TestFlight Dev">
                <Radio.Button value="dev">Dev (simulateur)</Radio.Button>
              </Tooltip>
            </Radio.Group>
          </ConfigProvider>
          <Radio.Group value={platform} onChange={(e) => setPlatform(e.target.value)} optionType="button" buttonStyle="solid">
            <Radio.Button value="ios">iOS</Radio.Button>
            <Radio.Button value="android">Android</Radio.Button>
          </Radio.Group>
        </Flex>

        <Tabs
          defaultActiveKey="qr"
          centered
          items={[
            {
              key: 'qr',
              label: 'QR code',
              children: (
                <Flex vertical gap={12} align="center">
                  <Typography.Text strong>Device physique</Typography.Text>
                  <div style={{ background: '#fff', padding: 12, borderRadius: 12, border: '1px solid #f0f0f0' }}>
                    <QRCode value={deepLink} size={180} />
                  </div>
                  <Typography.Text type="secondary">Scanne le QR code avec le device.</Typography.Text>
                </Flex>
              ),
            },
            {
              key: 'token',
              label: 'Token',
              children: (
                <Flex vertical gap={16}>
                  <Flex vertical gap={8}>
                    <Typography.Text strong>Token de connexion</Typography.Text>
                    <Typography.Text type="secondary">Colle ce token dans le champ mot de passe de l&apos;app.</Typography.Text>
                    <TextArea value={authorizationCode} rows={6} readOnly style={{ fontFamily: 'monospace', fontSize: 12, resize: 'none' }} />
                    <CopyToClipboard text={authorizationCode} onCopy={() => message.success('Token copié')}>
                      <Button type="primary" icon={<CopyOutlined />} style={{ alignSelf: 'center' }}>Copier le token</Button>
                    </CopyToClipboard>
                  </Flex>

                  <Flex vertical gap={8} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                    <Typography.Text strong>Simulateur (même machine)</Typography.Text>
                    <Typography.Text type="secondary">Colle la commande dans un terminal, l&apos;app s&apos;ouvre connectée.</Typography.Text>
                    <TextArea value={command} rows={5} readOnly style={{ fontFamily: 'monospace', fontSize: 12, resize: 'none' }} />
                    <CopyToClipboard text={command} onCopy={() => message.success('Commande copiée')}>
                      <Button icon={<CopyOutlined />} style={{ alignSelf: 'center' }}>Copier la commande</Button>
                    </CopyToClipboard>
                  </Flex>
                </Flex>
              ),
            },
          ]}
        />
      </Flex>
    </ConfigProvider>
  );
};

export default LoginAsMobileModalContent;
